import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminInquiry } from '../../../types';

interface InquiryRowProps {
  inquiry: AdminInquiry;
  isActive: boolean;
  onToggle: (inquiryId: string) => void;
}

const PREVIEW_LIMIT = 80;

const previewContent = (raw: string): string => {
  if (!raw) return '';
  if (raw.length <= PREVIEW_LIMIT) return raw;
  return `${raw.slice(0, PREVIEW_LIMIT)}…`;
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  return iso.slice(0, 10);
};

/**
 * Single collapsed row of the admin inquiry list. Clicking the row toggles
 * its active/expanded state in the parent page.
 */
const InquiryRow: React.FC<InquiryRowProps> = ({ inquiry, isActive, onToggle }) => {
  const { t } = useTranslation('admin');

  const name =
    inquiry.requestorName && inquiry.requestorName.trim().length > 0
      ? inquiry.requestorName
      : (t('inquiries.columns.nameFallback') as string);

  const userType = inquiry.requestorUserType
    ? (t(`inquiries.userType.${inquiry.requestorUserType}`, {
        defaultValue: inquiry.requestorUserType,
      }) as string)
    : '-';

  return (
    <tr
      onClick={() => onToggle(inquiry.inquiryId)}
      className={`border-t border-gray-100 cursor-pointer transition-colors ${
        isActive ? 'bg-blue-50/40' : 'hover:bg-gray-50'
      }`}
    >
      <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">{name}</td>
      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{userType}</td>
      <td className="px-4 py-3 text-gray-600">{previewContent(inquiry.content || '')}</td>
      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
        {formatDate(inquiry.createdAt)}
      </td>
    </tr>
  );
};

export default InquiryRow;
